// JobUpdateConfirmModal.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Divider,
  alpha,
  useTheme,
  Paper,
  List,
  ListItem,
  Chip,
  Avatar,
  IconButton,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { formatDateField, formatTime } from "../../../base/FormatTime";

const JobUpdateConfirmModal = ({
  open,
  onClose,
  onConfirm,
  onSkip,
  isLoading,
  pendingJobs = [],
  selectedEmployees = [],
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: "0px 32px 64px -12px rgba(0, 0, 0, 0.35)",
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          bgcolor: alpha(theme.palette.warning.main, 0.06),
          borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`,
          p: 3.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <WarningIcon sx={{ fontSize: 32, color: theme.palette.warning.main }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#0F172A">
              Update Scheduled Jobs
            </Typography>
            <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>
              You're about to change staff assignment for this weekly schedule.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
              <Chip
                label={`${pendingJobs.length} jobs affected`}
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  color: theme.palette.warning.main,
                }}
              />
              <Chip
                label={`${selectedEmployees.length} staff selected`}
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#94A3B8",
            "&:hover": { bgcolor: alpha("#94A3B8", 0.08) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3.5 }}>
          <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E" sx={{ mb: 2 }}>
            These scheduled visits will be updated with the new staff:
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
              borderRadius: "16px",
              overflow: "hidden",
              bgcolor: "#FAFBFC",
            }}
          >
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.04),
                borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.08)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption" fontWeight={600} color="#475467" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Pending Jobs
              </Typography>
              <Chip
                label={`${pendingJobs.length} jobs`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.55rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  color: theme.palette.warning.main,
                }}
              />
            </Box>

            <Box sx={{ maxHeight: 340, overflow: "auto", p: 1.5 }}>
              {pendingJobs.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="#94A3B8">
                    No pending jobs found
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {pendingJobs.map((job, index) => (
                    <ListItem
                      key={job.id}
                      sx={{
                        px: 2.5,
                        py: 2,
                        mb: 1,
                        borderRadius: "12px",
                        bgcolor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
                        },
                        flexDirection: "column",
                        alignItems: "stretch",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "10px",
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <EventIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                          </Box>
                          <Typography variant="body1" fontWeight={600} color="#0F172A">
                            {job.visit_name}
                          </Typography>
                        </Box>
                        <Chip
                          label={`#${job.id}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.5rem",
                            fontWeight: 500,
                            bgcolor: "#F1F5F9",
                            color: "#64748B",
                          }}
                        />
                      </Box>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, ml: 5.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "6px",
                              bgcolor: alpha("#3B82F6", 0.06),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 14, color: "#64748B" }} />
                          </Box>
                          <Typography variant="caption" color="#475467">
                            {formatTime(job.scheduled_start)} - {formatTime(job.scheduled_end)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "6px",
                              bgcolor: alpha("#8B5CF6", 0.06),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PersonIcon sx={{ fontSize: 14, color: "#64748B" }} />
                          </Box>
                          <Typography variant="caption" color="#475467">
                            {job.client_name}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={job.weekly_day}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.55rem",
                              fontWeight: 600,
                              bgcolor: alpha("#3B82F6", 0.08),
                              color: "#3B82F6",
                              borderRadius: "4px",
                            }}
                          />
                          <Chip
                            label={job.status_name || "Pending"}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.5rem",
                              fontWeight: 600,
                              bgcolor: alpha("#F59E0B", 0.12),
                              color: "#D97706",
                              borderRadius: "4px",
                            }}
                          />
                          <Chip
                            label={formatDateField(job.build_for)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.5rem",
                              fontWeight: 500,
                              bgcolor: "#F1F5F9",
                              color: "#64748B",
                              borderRadius: "4px",
                            }}
                          />
                        </Box>

                        {job.employee_details && job.employee_details.length > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="caption" color="#94A3B8" sx={{ fontWeight: 500 }}>
                              Current:
                            </Typography>
                            {job.employee_details.map((emp) => (
                              <Chip
                                key={emp.id}
                                avatar={<Avatar sx={{ width: 16, height: 16, fontSize: "0.4rem" }}>{emp.first_name?.[0]}</Avatar>}
                                label={`${emp.first_name} ${emp.last_name}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.55rem",
                                  fontWeight: 500,
                                  bgcolor: alpha("#94A3B8", 0.08),
                                  color: "#64748B",
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>

          <Box
            sx={{
              mt: 2.5,
              p: 2,
              bgcolor: alpha(theme.palette.info.main, 0.04),
              borderRadius: "12px",
              border: `1px solid ${alpha(theme.palette.info.main, 0.08)}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.info.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "14px" }}>ℹ️</Typography>
            </Box>
            <Typography variant="caption" color="#475467">
              <strong>Note:</strong> Jobs already in progress or completed will NOT be affected by this change.
              Only pending jobs will be updated with the new staff assignment.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <Divider sx={{ opacity: 0.4 }} />

      <DialogActions sx={{ p: 3.5, gap: 2 }}>
        <Button
          onClick={onSkip || onClose}
          disabled={isLoading}
          variant="outlined"
          sx={{
            flex: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "12px",
            borderColor: "#D0D5DD",
            color: "#344054",
            py: 1.5,
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Skip - Update Weekly Only
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          sx={{
            flex: 2,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "12px",
            bgcolor: theme.palette.warning.main,
            color: "#FFFFFF",
            py: 1.5,
            boxShadow: `0px 4px 16px ${alpha(theme.palette.warning.main, 0.25)}`,
            "&:hover": {
              bgcolor: theme.palette.warning.dark,
              boxShadow: `0px 6px 24px ${alpha(theme.palette.warning.main, 0.35)}`,
            },
          }}
        >
          {isLoading ? "Updating..." : `Update All ${pendingJobs.length} Jobs`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobUpdateConfirmModal;